import { useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { UserProfile } from '../types';

interface UseAvatarProps {
    onSuccess: (newProfile: UserProfile) => void;
    onError: (message: string) => void;
}

export const useAvatar = ({ onSuccess, onError }: UseAvatarProps) => {
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

    const handleAvatarChange = async (file: File) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            onError('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            onError('Image size must be less than 5MB');
            return;
        }

        try {
            setIsUploadingAvatar(true);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result as string);
            };
            reader.readAsDataURL(file);

            let avatarUrl: string;

            // Check upload configuration from backend
            const { data: uploadConfig } = await api.get('/users/upload-config');

            if (uploadConfig.s3Available) {
                // Use S3 upload
                try {
                    const { data: presignedData } = await api.post('/users/avatar/upload-url', {
                        contentType: file.type,
                    });

                    // Upload to S3 using presigned URL with streaming
                    const s3Response = await fetch(presignedData.uploadUrl, {
                        method: 'PUT',
                        body: file,
                    });

                    if (!s3Response.ok) {
                        const errorText = await s3Response.text();
                        throw new Error(`S3 upload failed: ${s3Response.status} - ${errorText}`);
                    }

                    avatarUrl = presignedData.fileUrl;
                } catch (s3Error) {
                    console.error('S3 upload error:', s3Error);
                    throw new Error('Failed to upload to S3');
                }
            } else {
                // Use local upload
                const formData = new FormData();
                formData.append('file', file);

                const { data: localUploadData } = await api.post('/users/avatar/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                const baseURL = api.defaults.baseURL || '';
                avatarUrl = `${baseURL}${localUploadData.fileUrl}`;
            }

            // Update user profile with new avatar URL
            const { data: updatedProfile } = await api.put<UserProfile>('/users/profile', {
                avatar: avatarUrl,
            });

            onSuccess(updatedProfile);
            setAvatarPreview(null);
        } catch (err: unknown) {
            console.error('Avatar upload error:', err);
            onError(getErrorMessage(err));
            setAvatarPreview(null);
        } finally {
            setIsUploadingAvatar(false);
        }
    };

    return {
        isUploadingAvatar,
        avatarPreview,
        handleAvatarChange
    };
};
